import type { ClientProfile } from '@/types/company';
import type { Recruiter } from '@/types/recruiter';
import type { AvailableClientProfile, User } from '@/types/user';

export type ImpersonationSelectValues = {
  selectedClientProfileId: string;
  selectedRecruiterId: string;
};

export function resolveImpersonationSelectValues({
  isImpersonating,
  isClient,
  isRecruiter,
  userId,
  clientProfileId,
  availableClientProfileIds = [],
}: {
  isImpersonating: boolean;
  isClient: boolean;
  isRecruiter: boolean;
  userId?: string;
  clientProfileId?: string | null;
  availableClientProfileIds?: string[];
}): ImpersonationSelectValues {
  if (!isImpersonating) {
    return { selectedClientProfileId: '', selectedRecruiterId: '' };
  }
  if (isClient) {
    const fallbackProfileId =
      availableClientProfileIds.length === 1
        ? availableClientProfileIds[0]
        : '';
    const profileId = clientProfileId || fallbackProfileId;
    if (profileId) {
      return {
        selectedClientProfileId: profileId,
        selectedRecruiterId: '',
      };
    }
  }
  if (isRecruiter && userId) {
    return {
      selectedClientProfileId: '',
      selectedRecruiterId: userId,
    };
  }
  return { selectedClientProfileId: '', selectedRecruiterId: '' };
}

export function mapAvailableProfileToClientProfile(
  profile: AvailableClientProfile,
  userId: string,
): ClientProfile {
  return {
    id: profile.id,
    department: profile.department,
    user_id: userId,
    name: profile.name || '',
    email: profile.email || '',
    company: {
      id: profile.company_id,
      name: profile.company_name,
      image: profile.company_image || '',
    },
  };
}

export function currentClientProfileOption(
  user: User | null | undefined,
  selectedClientProfileId: string,
): ClientProfile | null {
  if (!selectedClientProfileId || !user) {
    return null;
  }
  const available = user.available_client_profiles?.find(
    (profile) => profile.id === selectedClientProfileId,
  );
  if (available) {
    return mapAvailableProfileToClientProfile(available, user.id);
  }
  return {
    id: selectedClientProfileId,
    user_id: user.id,
    name: user.name || '',
    email: user.email || '',
    department: user.department || '',
    company: {
      id: user.company?.id || '',
      name: user.company?.name || '',
      image: user.company?.image || '',
    },
  };
}

export function currentRecruiterOption(
  user: User | null | undefined,
  selectedRecruiterId: string,
): Recruiter | null {
  if (!selectedRecruiterId || !user || user.id !== selectedRecruiterId) {
    return null;
  }
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    phone: user.phone || '',
    image: user.image || '',
    title: user.title || '',
    recruiter_type: user.recruiter_type || '',
    onboarding_status: user.onboarding_status || '',
    company: user.company
      ? { id: user.company.id, name: user.company.name }
      : null,
    city: null,
    locations: [],
    specializations: [],
    registered_at: '',
    last_login: '',
    own_recruiter: false,
    managed_recruiter: false,
    fee_currency: '',
    profile_id: '',
    statistics: { hired: 0, in_process: 0, uploaded: 0, rejected: 0 },
  };
}

export function mergeOptionIfMissing<T extends { id: string }>(
  rows: T[],
  current: T | null,
): T[] {
  if (!current) {
    return rows;
  }
  if (rows.some((row) => row.id === current.id)) {
    return rows;
  }
  return [current, ...rows];
}
